import { useState, useEffect } from "react";

import { Modal } from "antd";
import VLSliderWithInput from "views/uikit/VLSliderWithInput";
import VLButton from "views/uikit/Buttons/VLButton";
import {
  ResourceConfiguration,
  DEFAULT_RESOURCE_CONFIG,
  PRESET_CONFIGS,
  formatDiskDisplayText,
} from "../../../helpers/constants/resourceConfiguration";

import styles from "./style.module.scss";

interface ResourceConfigurationModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  onSave: (config: ResourceConfiguration) => void;
  initialConfig?: ResourceConfiguration;
}

const ResourceConfigurationModal = ({
  showModal,
  setShowModal,
  onSave,
  initialConfig = DEFAULT_RESOURCE_CONFIG,
}: ResourceConfigurationModalProps) => {
  const [config, setConfig] = useState<ResourceConfiguration>(initialConfig);
  const [isDiskEnabled, setIsDiskEnabled] = useState(
    initialConfig.disk !== null,
  );

  useEffect(() => {
    if (showModal) {
      setConfig(initialConfig);
      setIsDiskEnabled(initialConfig.disk !== null);
    }
  }, [showModal, initialConfig]);

  const handleCpuChange = (value: number) => {
    setConfig((prev: ResourceConfiguration) => ({ ...prev, cpu: value }));
  };

  const handleMemoryChange = (value: number) => {
    setConfig((prev: ResourceConfiguration) => ({ ...prev, memory: value }));
  };

  const handleDiskChange = (value: number) => {
    setConfig((prev: ResourceConfiguration) => ({ ...prev, disk: value }));
  };

  const handleDiskToggle = (enabled: boolean) => {
    setIsDiskEnabled(enabled);
    setConfig((prev: ResourceConfiguration) => ({
      ...prev,
      disk: enabled ? prev.disk || 50 : null,
    }));
  };

  const applyPreset = (presetName: keyof typeof PRESET_CONFIGS) => {
    const preset = PRESET_CONFIGS[presetName];
    setConfig(preset);
    setIsDiskEnabled(preset.disk !== null);
  };

  const handleSave = () => {
    onSave({
      ...config,
      disk: isDiskEnabled ? config.disk : null,
    });
    setShowModal(false);
  };

  const handleCancel = () => {
    setConfig(initialConfig);
    setIsDiskEnabled(initialConfig.disk !== null);
    setShowModal(false);
  };

  const resetToDefaults = () => {
    setConfig(DEFAULT_RESOURCE_CONFIG);
    setIsDiskEnabled(false);
  };

  return (
    <Modal
      width="50%"
      centered={true}
      title={
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
          <div className={styles.headerText}>
            <h2 className={styles.title}>Configure Resources</h2>
            <div className={styles.subtitle}>
              <span className={styles.primaryText}>
                Set computational resources for your dataset processing
                pipeline.
              </span>
              <span className={styles.secondaryText}>
                If not configured, resources are auto-deduced from dataset size.
                Manual settings act as minimums — higher allocations will be
                used when needed.
              </span>
            </div>
          </div>
        </div>
      }
      footer={
        <div className={styles.modalFooter}>
          <VLButton onClick={resetToDefaults} type="default">
            Reset to Defaults
          </VLButton>

          <div className={styles.actionButtons}>
            <VLButton type="default" text="Cancel" onClick={handleCancel} />
            <VLButton
              type="primary"
              text="Save Configuration"
              onClick={handleSave}
            />
          </div>
        </div>
      }
      destroyOnHidden
      open={showModal}
      onCancel={handleCancel}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalBody}>
          {/* Preset Configurations */}
          <div className={styles.presetsSection}>
            <h3 className={styles.sectionTitle}>Quick Presets</h3>
            <div className={styles.presetButtons}>
              {Object.entries(PRESET_CONFIGS).map(
                ([key, preset]: [string, ResourceConfiguration]) => (
                  <button
                    key={key}
                    className={styles.presetButton}
                    onClick={() =>
                      applyPreset(key as keyof typeof PRESET_CONFIGS)
                    }
                  >
                    <div className={styles.presetName}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </div>
                    <div className={styles.presetSpecs}>
                      {preset.cpu}CPU • {preset.memory}GB •{" "}
                      {formatDiskDisplayText(preset.disk)}
                    </div>
                  </button>
                ),
              )}
            </div>
          </div>

          {/* Custom Configuration */}
          <div className={styles.customSection}>
            <h3 className={styles.sectionTitle}>Custom Configuration</h3>

            {/* CPU Configuration */}
            <VLSliderWithInput
              label="CPU Cores"
              value={config.cpu}
              min={0.1}
              max={64}
              step={0.1}
              onValueChange={handleCpuChange}
            />

            {/* Memory Configuration */}
            <VLSliderWithInput
              label="Memory"
              value={config.memory}
              min={4}
              max={224}
              step={1}
              unit="Gi"
              onValueChange={handleMemoryChange}
            />

            {/* Disk Configuration */}
            <div className={styles.diskSection}>
              <div className={styles.diskToggle}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isDiskEnabled}
                    onChange={(e) => handleDiskToggle(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>
                    Configure disk storage
                  </span>
                </label>
              </div>

              {isDiskEnabled && (
                <VLSliderWithInput
                  label="Disk Storage"
                  value={config.disk || 50}
                  min={1}
                  max={1024}
                  step={1}
                  unit="Gi"
                  onValueChange={handleDiskChange}
                />
              )}
            </div>
          </div>

          {/* Resource Summary */}
          <div className={styles.summarySection}>
            <h3 className={styles.sectionTitle}>Resource Summary</h3>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryItem}>
                <div className={styles.summaryLabel}>CPU</div>
                <div className={styles.summaryValue}>
                  {config.cpu.toFixed(1)} cores
                </div>
              </div>
              <div className={styles.summaryItem}>
                <div className={styles.summaryLabel}>Memory</div>
                <div className={styles.summaryValue}>{config.memory} Gi</div>
              </div>
              <div className={styles.summaryItem}>
                <div className={styles.summaryLabel}>Disk</div>
                <div className={styles.summaryValue}>
                  {isDiskEnabled ? `${config.disk} Gi` : "Default disk"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ResourceConfigurationModal;
